import type { BaseSyntheticEvent, ReactElement } from 'react';
import { cloneElement, useEffect, useRef } from 'react';
import type {
  DecimalSeparatorType,
  LanguageCode,
  ThousandsSeparatorType
} from '@elfsight/configurator-contracts';
import { formatSuffixNumber } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import type {
  Field,
  FileFieldValue,
  FormField,
  FormStatus,
  ImageChoiceOption
} from '../contexts';
import { useFormDispatch, useFormSelector } from '../contexts';
import { EXCLUDED_FIELDS_FROM_SUBMIT } from './Form';
import {
  ERROR_TYPE,
  EXTENSION_ERROR_TYPE,
  useRecaptchaPlatform,
  useUploadFiles
} from './hooks';
import {
  checkUpdatedFields,
  type FormatSubmitData,
  submit,
  useHandleFieldsErrors
} from './utils';

const SELECT_FIELDS = ['choice', 'imageChoice', 'dropdown'];

type ExtraSubmitProps = Record<string, unknown>;

export interface AdditionalData {
  widget?: string | null;
  integrations: unknown[];
  widget_token?: string;
  settings_pid: string | undefined;
  extra?: ExtraSubmitProps;
}

export interface FormContainerProps {
  widgetLanguage?: LanguageCode;
  children: ReactElement;
  fields: FormField[];
  displayedFields: FormField[];
  submitUrl: string;
  additionalData: AdditionalData;
  onSubmit(index: () => Promise<FormStatus>): Promise<string | undefined>;
  recaptcha?: boolean;
  formatSubmitData?: FormatSubmitData;
}

export function FormContainer({
  children,
  fields,
  widgetLanguage,
  displayedFields,
  submitUrl,
  additionalData,
  recaptcha = false,
  onSubmit,
  formatSubmitData
}: FormContainerProps) {
  const { t } = useLingui();
  const consentValues: [string, string] = [t`Yes`, t`No`];
  const prevFieldsRef = useRef<FormField[]>([]);
  const { fields: changedFields } = useFormSelector();
  const dispatch = useFormDispatch();

  useRecaptchaPlatform(recaptcha);
  const { startFileWatcher, uploadFiles } = useUploadFiles(
    changedFields,
    additionalData.widget_token
  );

  const handleFieldsErrors = useHandleFieldsErrors();

  useEffect(() => {
    return startFileWatcher();
  }, [startFileWatcher]);

  const formatValue = (field: Field) => {
    const calculatorField = fields.find(
      ({ id, isCalculatorField }) => id === field.id && isCalculatorField
    );

    if (calculatorField) {
      return formatCalculatorField(calculatorField, fields);
    }

    const displayedField = displayedFields.find(({ id }) => id === field.id);
    const isShown = !!displayedField;

    const derivedField = {
      ...field,
      hidden: !isShown,
      value: isShown ? field.value : undefined,
      fieldId: field.fieldID
    };

    delete derivedField.fieldID;

    if (field.type === 'number' && typeof derivedField.value === 'number') {
      return { ...derivedField, value: String(derivedField.value) };
    }

    if (field.type === 'file' || field.type === 'signature') {
      return formatFileValue(derivedField);
    }

    if (field.type === 'consent') {
      return formatConsentValue(derivedField, consentValues);
    }

    if (SELECT_FIELDS.includes(field.type as string)) {
      return formatSelectValue(derivedField, fields);
    }

    return derivedField;
  };

  const submitForm = async (extra?: ExtraSubmitProps) => {
    const entries = Object.entries(changedFields).filter(
      ([_, { type }]: [string, Field]) =>
        type && !EXCLUDED_FIELDS_FROM_SUBMIT.includes(type)
    );

    const signatureFieldIds = Object.entries(changedFields)
      .filter(([_, field]) => field.type === 'signature')
      .map(([id]) => id);

    if (signatureFieldIds.length > 0) {
      await uploadFiles(signatureFieldIds);
    }

    const formattedEntries = entries.map(([key, value]) => [
      key,
      formatValue(value)
    ]);

    const filteredFields = Object.fromEntries(formattedEntries);

    const { status } = await submit({
      formatSubmitData,
      url: submitUrl,
      data: { ...additionalData, extra },
      fields: filteredFields
    });

    dispatch({
      status,
      type: 'setStatus'
    });

    if (status === 'success') {
      dispatch({
        type: 'setStarted',
        started: false
      });
    }

    return status as FormStatus;
  };

  const handleSubmit = (e?: BaseSyntheticEvent, extra?: ExtraSubmitProps) => {
    if (e?.preventDefault) e.preventDefault();

    return onSubmit(async () => {
      dispatch({
        type: 'setStatus',
        status: 'pending'
      });

      const fieldsError = await handleFieldsErrors(recaptcha);

      if (fieldsError) {
        return fieldsError;
      }

      return await submitForm(extra);
    });
  };

  useEffect(() => {
    const isUpdated = checkUpdatedFields(prevFieldsRef.current, fields);

    if (isUpdated) {
      prevFieldsRef.current = fields;

      dispatch({
        type: 'init',
        fields
      });
    }
  }, [fields, dispatch]);

  return cloneElement(children, {
    ...children.props,
    displayedFields,
    fields,
    widgetLanguage,
    onSubmit: handleSubmit
  });
}

function formatFileValue(field: Field) {
  const fileValue = field.value as FileFieldValue;

  if (!fileValue || fileValue?.length === 0) {
    return {
      ...field,
      value: ''
    };
  }

  const fileUrls = Array.from(fileValue)
    .filter(
      ({ uploaded }) =>
        uploaded !== ERROR_TYPE && uploaded !== EXTENSION_ERROR_TYPE
    )
    .map(({ uploaded }) => uploaded);

  return {
    ...field,
    value: fileUrls
  };
}

function formatCalculatorField(
  field: FormField & {
    hidden?: boolean;
    thousandsSeparator?: ThousandsSeparatorType;
    decimalSeparator?: DecimalSeparatorType;
  },
  fields: FormField[]
) {
  const {
    id,
    fieldID,
    name,
    label,
    placeholder,
    required,
    type,
    group,
    hidden,
    thousandsSeparator,
    decimalSeparator
  } = field;
  const autoRespondHidden = 'visible' in field ? !field.visible : false;

  const fieldValue = SELECT_FIELDS.includes(type)
    ? formatSelectValue(field, fields).value
    : formatCalculatorValue(field, thousandsSeparator, decimalSeparator);

  const fieldName =
    [name, label, placeholder].find((fieldName) => !!fieldName?.trim()) ||
    'Untitled';

  return {
    fieldId: fieldID,
    id,
    name: fieldName,
    required,
    type,
    value: hidden ? undefined : fieldValue,
    hidden,
    group: group,
    autoRespondHidden
  };
}

function formatCalculatorValue(
  field: FormField,
  thousandsSeparator?: ThousandsSeparatorType,
  decimalSeparator?: DecimalSeparatorType
) {
  const { value, format, group, maximumFormatFractionDigits } = field;
  if (!('format' in field) || format?._mode === 'string') {
    return value;
  }

  const numberValue = Number(value);

  if (isNaN(numberValue) || !isFinite(numberValue)) {
    return '—';
  }

  if (field.type === 'slider') {
    return formatSuffixNumber(numberValue, {
      prefix: format?.prefix,
      suffix: format?.suffix,
      maximumFractionDigits: maximumFormatFractionDigits,
      thousandsSeparator,
      decimalSeparator
    });
  }

  const isResultField = group === 'calculation-results';

  return formatSuffixNumber(numberValue, {
    prefix: format?.prefix,
    suffix: format?.suffix,
    minimumFractionDigits: isResultField ? format?.decimalPlaces : undefined,
    maximumFractionDigits: isResultField ? format?.decimalPlaces : undefined,
    thousandsSeparator,
    decimalSeparator
  });
}

function formatConsentValue(field: Field, consentValues: [string, string]) {
  const [yesValue, noValue] = consentValues;
  if (field.value === 'Yes') {
    return {
      ...field,
      value: yesValue
    };
  }

  return {
    ...field,
    value: noValue
  };
}

function formatSelectValue(field: Field, fields: FormField[]) {
  const fieldOptions = fields.find((item) => item.id === field.id)?.options;
  const getOptionName = (value: string | undefined) => {
    // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const option = fieldOptions?.find(
      ({ id: optionID }: { id: string }) => optionID === value
    );

    if (field.type === 'choice') {
      return option?.name || value;
    }

    if (field.type === 'imageChoice') {
      return option?.name || (option as ImageChoiceOption)?.index;
    }

    return option?.name;
  };

  const selectedValue = Array.isArray(field.value)
    ? field.value.map((value) => getOptionName(value as string))
    : getOptionName(field.value as string);

  return {
    ...field,
    value: selectedValue
  };
}
